import { PlusOutlined } from '@ant-design/icons';
import { Alert, Button, Form, Input, Typography } from 'antd';
import React from 'react';
import { DEFAULT_MATCH_COMPETITION_STAGES } from '../../../helpers/matchCompetitionStages';
import { errorWithUserMessage, success } from '../../../helpers/NotificationHelper';
import {
    ApiException,
    CompetitionDetailVm,
    CompetitionsClient,
    UpdateCompetitionCommandOfCompetition,
} from '../../../utils/api';

export interface CompetitionMatchStagesTabProps {
    competition: CompetitionDetailVm;
    canEdit: boolean;
    onSaved: () => void | Promise<void>;
}

function buildUpdateCommand(
    c: CompetitionDetailVm,
    extraCompetitionStages: string[]
): UpdateCompetitionCommandOfCompetition {
    return new UpdateCompetitionCommandOfCompetition({
        id: c.id,
        name: c.name,
        nameUniversal: c.nameUniversal,
        shortName: c.shortName,
        description: c.description,
        extraCompetitionStages,
        // season is required on the command (NotEmpty validator surfaced into the schema),
        // but legacy competitions may not have one — pass through whatever the detail has.
        season: c.season!,
        startDate: c.startDate,
        endDate: c.endDate,
        venue: c.venue,
        address: c.address,
        web: c.web,
        email: c.email,
        phone: c.phone,
        facebook: c.facebook,
        youtube: c.youtube,
        twitter: c.twitter,
        instagram: c.instagram,
        logo: c.logo,
        matchLogTemplateLanguage: c.matchLogTemplateLanguage || 'en',
        isPro: c.isPro,
        isInternational: c.isInternational,
        gameLogPinConfirmationRequired: c.gameLogPinConfirmationRequired,
        matchSegmentId: c.matchSegmentId,
        matchSegmentLength: c.matchSegmentLength,
        shotClockMaxSeconds: c.shotClockMaxSeconds,
        shotClockResetSeconds: c.shotClockResetSeconds,
        exclusionDurationSeconds: c.exclusionDurationSeconds,
        brutalityDurationSeconds: c.brutalityDurationSeconds,
        federationId: c.federationId,
        categoryId: c.categoryId,
        competitionTypeId: c.competitionTypeId,
        countryId: c.countryId,
        cityId: c.cityId,
    });
}

const CompetitionMatchStagesTab: React.FC<CompetitionMatchStagesTabProps> = ({
    competition,
    canEdit,
    onSaved,
}) => {
    const [form] = Form.useForm();
    const [saving, setSaving] = React.useState(false);

    React.useEffect(() => {
        form.setFieldsValue({
            extraCompetitionStages:
                competition.extraCompetitionStages && competition.extraCompetitionStages.length > 0
                    ? competition.extraCompetitionStages
                    : [],
        });
    }, [competition, form]);

    const onFinish = async (values: { extraCompetitionStages?: string[] }): Promise<void> => {
        const raw = values.extraCompetitionStages || [];
        const extraCompetitionStages = raw
            .map((s) => (typeof s === 'string' ? s.trim() : ''))
            .filter((s) => s.length > 0);

        setSaving(true);
        try {
            const client = new CompetitionsClient();
            const cmd = buildUpdateCommand(competition, extraCompetitionStages);
            await client.update(competition.id, cmd);
            success('Extra competition stages saved.');
            await onSaved();
        } catch (e) {
            if (e instanceof ApiException) {
                errorWithUserMessage(e.response);
            } else {
                errorWithUserMessage('Could not save.');
            }
        } finally {
            setSaving(false);
        }
    };

    return (
        <div style={{ maxWidth: 560 }}>
            <Typography.Title level={4} style={{ marginBottom: 12 }}>
                Match competition stages
            </Typography.Title>
            <Alert
                type="info"
                showIcon
                style={{ marginBottom: 12, padding: '8px 12px' }}
                message={
                    <span style={{ fontSize: 13 }}>
                        Built-in options (always available):{' '}
                        <Typography.Text code style={{ fontSize: 12 }}>
                            {DEFAULT_MATCH_COMPETITION_STAGES.join(', ')}
                        </Typography.Text>
                    </span>
                }
                description={
                    <span style={{ fontSize: 12, marginTop: 6, display: 'block' }}>
                        Add rows below only for <strong>extra</strong> labels (e.g. D1, Playoffs).
                        Each row is one match dropdown option.
                    </span>
                }
            />
            {canEdit ? (
                <Form form={form} layout="vertical" onFinish={onFinish}>
                    <Typography.Text
                        type="secondary"
                        style={{ fontSize: 12, display: 'block', marginBottom: 6 }}
                    >
                        Extra labels
                    </Typography.Text>
                    <Form.List name="extraCompetitionStages">
                        {(fields, { add }): React.ReactElement => (
                            <>
                                {fields.map((field) => (
                                    <Form.Item
                                        {...field}
                                        key={field.key}
                                        rules={[{ max: 200, message: 'Max 200 characters!' }]}
                                        style={{ marginBottom: 6 }}
                                    >
                                        <Input size="small" placeholder="e.g. Group D1" />
                                    </Form.Item>
                                ))}
                                <Form.Item style={{ marginBottom: 8 }}>
                                    <Button
                                        type="dashed"
                                        size="small"
                                        onClick={(): void => add()}
                                        icon={<PlusOutlined />}
                                    >
                                        Add row
                                    </Button>
                                </Form.Item>
                            </>
                        )}
                    </Form.List>
                    <Form.Item style={{ marginBottom: 0 }}>
                        <Button type="primary" size="small" htmlType="submit" loading={saving}>
                            Save extra stages
                        </Button>
                    </Form.Item>
                </Form>
            ) : (
                <>
                    <Typography.Paragraph>
                        You do not have permission to edit this competition.
                    </Typography.Paragraph>
                    {(competition.extraCompetitionStages || []).length > 0 ? (
                        <ul>
                            {competition.extraCompetitionStages!.map((s) => (
                                <li key={s}>{s}</li>
                            ))}
                        </ul>
                    ) : (
                        <Typography.Text type="secondary">
                            No extra stages configured.
                        </Typography.Text>
                    )}
                </>
            )}
        </div>
    );
};

export default CompetitionMatchStagesTab;
